import { Field, FieldType } from '../../../features/AssetGrid/types/map.types';
import {
	excludedFilterColumn,
	spatialFilterColumn,
} from '../../../features/AssetGrid/utils/columnMapping';

import { createSelector } from '@reduxjs/toolkit';
import { isValidFieldType } from '../../../features/AssetGrid/utils/helpers';
import { selectSpatialLayerMeta } from '../../risk/selectors';
import { assetSchemaSelectorByType } from '@innovyze/lib_asset_schema_store';
import { SingleProperty } from '@innovyze/lib_asset_schema';
import { getFieldTypeMapping } from './getFieldTypeMapping';

const DEFAULT_COLUMN_WIDTH = 150;

const isVisibleByDefault = (
	property: SingleProperty,
	useMapPanelCapability: boolean,
): boolean =>
	useMapPanelCapability ? property.hasCapability('ShowOnMapPanel') : true;

const mapAssetPropertyToField = (useMapPanelCapability: boolean) => (
	property: SingleProperty,
): Field => {
	const { fieldType, precision, multiline } = getFieldTypeMapping(property);
	return {
		name: property.displayName,
		fieldKey: property.id,
		fieldType,
		columnWidth: DEFAULT_COLUMN_WIDTH,
		precision,
		units: property.units,
		multiline,
		visible: isVisibleByDefault(property, useMapPanelCapability),
	};
};

const calculateVisibility = (columns: Pick<Field, 'fieldKey' | 'visible'>[]) =>
	columns.reduce<Record<string, boolean>>((visibility, column) => {
		visibility[column.fieldKey] = column.visible ?? false;
		return visibility;
	}, {});

export const selectAssetGridColumnLayout = createSelector(
	[assetSchemaSelectorByType],
	assetSchema => {
		const properties = assetSchema?.getProperties() ?? [];
		const flattenedProperties = properties.flatMap(property =>
			property.type === 'List' ? property.properties : [property],
		);

		const useMapPanelCapability = flattenedProperties.some(property =>
			property.hasCapability('ShowOnMapPanel'),
		);

		const propertyColumns: Field[] = flattenedProperties.map(
			mapAssetPropertyToField(useMapPanelCapability),
		);

		const columns = [
			...propertyColumns,
			spatialFilterColumn,
			excludedFilterColumn,
		];

		return { columns, visibility: calculateVisibility(columns) };
	},
);

export const selectSpatialDataColumnLayout = createSelector(
	[selectSpatialLayerMeta],
	layerMeta => {
		const propertyColumns = layerMeta.map(({ property, type }) => ({
			fieldKey: property,
			fieldType: correctFieldType(type),
			name: property,
			visible: true,
			columnWidth: DEFAULT_COLUMN_WIDTH,
		}));

		const columns = [
			...propertyColumns,
			spatialFilterColumn,
			excludedFilterColumn,
		];

		return { columns, visibility: calculateVisibility(columns) };
	},
);

const correctFieldType = (fieldType: string): FieldType => {
	if (fieldType === 'datetime' || fieldType === 'time') {
		return FieldType.Date;
	}
	if (isValidFieldType(fieldType)) {
		return fieldType as FieldType;
	}
	return FieldType.String;
};
